import React, { PureComponent } from 'react';
import Modal from '../../../components/Modals/Modal.js';
import Loader from '../../../components/Loader.js';
import Enrolment from './Enrolment.js';
import CoverageSelection from './CoverageSelection.js';
import Confirmation from './Confirmation.js';
import Agreement from './Agreement.js';
import * as Services from '../../../services/index.js';
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
class PurchaseFlow extends PureComponent {
  state = {
    step: 0,
    purchaseData: {
      ...this.props.selectedProduct.productPurchaseTemplate,
      signage: {
        name: '',
        signature: '',
        acceptedTerms: false,
        timestampOfAcceptance: null,
        datedOn: ''
      },
      password: '',
      memberData: {
        ...this.props.member
      }
    },
    enrolmentErrors: [],
    confirmationErrors: {
      agreement: false,
      signage: false,
      password: false,
      name: false,
      signature: false,
      datedOn: false
    }
  };

  /**
   * For each product enrolment question, ensure we have saved an answer for each one
   *
   * @function
   * @returns {boolean} - Returns `true` if all enrolment questions have answers, `false` otherwise.
   * @private
   */
  _validateEnrolmentQuestions = () => {
    const enrolmentErrors = {};
    const questionIDs = Object.keys(this.props.selectedProduct.enrolmentQuestions);
    for (const questionID of questionIDs) {
      if (this.state.purchaseData.enrolmentQuestions[questionID].answer === null) {
        enrolmentErrors[questionID] = true;
      }
    }
    this.setState({
      enrolmentErrors
    });
    return Object.keys(enrolmentErrors).length === 0;
  };
  _validateSignageBlock = async () => {
    const passwordError = this.props.isMasterSite ? null : await Services.validateInput('checkEmpty', this.state.purchaseData.password);
    let signageNameError = await Services.validateInput('checkEmpty', this.state.purchaseData.signage.name);
    const signageSignatureError = await Services.validateInput('checkEmpty', this.state.purchaseData.signage.signature);
    const signageDatedOnError = await Services.validateInput('date', this.state.purchaseData.signage.datedOn, i18n.locale);
    const acceptedTerms = this.state.purchaseData.signage.acceptedTerms === false;
    if (this.state.purchaseData.signage.name !== this.state.purchaseData.signage.signature) {
      signageNameError = 'The name and signature must match.';
    }
    this.setState({
      confirmationErrors: {
        ...this.state.confirmationErrors,
        name: signageNameError,
        signature: signageSignatureError,
        datedOn: signageDatedOnError,
        password: passwordError,
        agreement: acceptedTerms
      }
    });
    if (signageNameError || signageSignatureError || signageDatedOnError || passwordError || acceptedTerms) {
      return false;
    }
    return true;
  };

  // eslint-disable-next-line consistent-return
  _nextStep = () => {
    let stepIncrease = 1;

    // if we are moving on from the enrolment step, ensure we have answers to all the enrolment questions
    if (this.state.step === 0) {
      const enrolmentValid = this._validateEnrolmentQuestions();
      if (!enrolmentValid) {
        return null;
      }
      if (this.props.selectedProduct.pricingType === 'static') {
        stepIncrease = 2;
      }
    }
    this.setState({
      step: this.state.step + stepIncrease
    });
  };
  _previousStep = () => {
    let stepDecrease = 1;

    // if we moving back from the confirmation page, make sure we don't go to the coverage step if it's static
    if (this.state.step === 2) {
      if (this.props.selectedProduct.pricingType === 'static') {
        stepDecrease = 2;
      }
    }
    this.setState({
      step: this.state.step - stepDecrease
    });
  };
  _savePurchaseData = data => {
    this.setState({
      purchaseData: {
        ...data
      }
    });
  };

  /**
   * Initiates the product purchase process, validates the signage block, and completes the purchase if successful.
   *
   * @async
   * @function
   */
  _purchaseProduct = async () => {
    const signageValid = await this._validateSignageBlock();
    if (!signageValid) {
      return false;
    }

    // if it's the master site, we don't need the password for validation
    if (this.props.isMasterSite) {
      delete this.state.purchaseData.password;
    }

    // we don't need to save the memberData;
    delete this.state.purchaseData.memberData;
    const success = await this.props.purchaseProduct({
      ...this.state.purchaseData,
      signage: {
        ...this.state.purchaseData.signage,
        datedOn: Services.dates.formatOutgoingDate(this.state.purchaseData.signage.datedOn)
      }
    });
    if (success) {
      this.props.onClose();
    }
    return null;
  };
  render() {
    if (!this.state.purchaseData) {
      return null;
    }
    const step = this.state.step;
    const product = this.props.selectedProduct;
    return /*#__PURE__*/React.createElement(Modal, {
      medium: true,
      open: true,
      onClose: this.props.onClose,
      title: step === 0 ? i18n.t(tokens.labels.marketplace.welcomeToTheSimplyBenefitsMarketplace) : step === 1 ? i18n.t(tokens.labels.marketplace.selectCoverage) : step === 2 ? i18n.t(tokens.labels.confirmation) : i18n.t(tokens.labels.marketplace.agreement),
      onBack: step === 0 ? null : this._previousStep,
      primaryButton: {
        label: this.state.step === 3 ? i18n.t(tokens.buttons.completePurchase) : this.state.step === 2 ? i18n.t(tokens.buttons.confirmPurchase) : i18n.t(tokens.buttons.next),
        onClick: step < 3 ? this._nextStep : this._purchaseProduct
      },
      loader: this.props.loader
    }, step === 0 && /*#__PURE__*/React.createElement(Enrolment, {
      product: product,
      member: this.state.purchaseData.memberData,
      purchaseData: this.state.purchaseData,
      enrolmentErrors: this.state.enrolmentErrors,
      savePurchaseData: this._savePurchaseData
    }), step === 1 && /*#__PURE__*/React.createElement(CoverageSelection, {
      member: this.state.purchaseData.memberData,
      product: product,
      purchaseData: this.state.purchaseData,
      savePurchaseData: this._savePurchaseData,
      getRate: this.props.getRate
    }), step === 2 && /*#__PURE__*/React.createElement(Confirmation, {
      member: this.state.purchaseData.memberData,
      product: product,
      purchaseData: this.state.purchaseData,
      whitelabel: this.props.whitelabel
    }), step === 3 && /*#__PURE__*/React.createElement(Agreement, {
      isMasterSite: this.props.isMasterSite,
      member: this.state.purchaseData.memberData,
      product: product,
      purchaseData: this.state.purchaseData,
      savePurchaseData: this._savePurchaseData,
      whitelabel: this.props.whitelabel,
      confirmationErrors: this.state.confirmationErrors
    }), this.props.loader?.isLoading && this.props.loader.type === 'container' && /*#__PURE__*/React.createElement(Loader, {
      isContainer: true
    }));
  }
}
export default PurchaseFlow;